/**
 * ActionCenterPage — admin route hosting both the Action Center and the
 * Alerts surface behind a lightweight tab switcher.
 *
 * Rationale: the sidebar dropped its standalone "Alertas" item so admins
 * reach both surfaces from a single entry (Centro de acción). Each tab
 * mounts the pre-existing component — no logic is duplicated, no data
 * fetch is shared. Marketing and Constructor still consume Alerts from
 * their own dashboards; the /alerts route stays live for compatibility.
 *
 * Tab state syncs to the `?tab=` query param so `/actions?tab=alerts` is
 * a stable deep link.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminShell from "@/pages/admin/AdminShell";
import ActionCenter from "@/components/ActionCenter";
import { AlertsBoard } from "@/components/NextBestAction";

const TABS = [
  { key: "actions", label: "Acciones" },
  { key: "alerts",  label: "Alertas"  },
];

// AlertsBoard emits keys like "leads" / "payments" / … via onNavigate.
// Preserve the same routing map the old AlertsPage used so behaviour is
// identical for users landing on the Alertas tab here.
const ALERT_NAV_MAP = {
  leads:    "/leads",
  payments: "/payments",
  progress: "/villas",
  tasks:    "/tasks",
  dataroom: "/documents",
};

export default function ActionCenterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "alerts" ? "alerts" : "actions";
  const [tab, setTab] = useState(initialTab);

  // Keep the URL in sync so the tab is deep-linkable and survives reloads.
  // `replace: true` avoids polluting history on every toggle.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (tab === "alerts") next.set("tab", "alerts");
    else next.delete("tab");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleAlertsNavigate = (key) => {
    navigate(ALERT_NAV_MAP[key] || "/actions?tab=alerts");
  };

  return (
    <AdminShell>
      <div
        className="mb-4 flex gap-2"
        role="tablist"
        aria-label="Centro de acción"
        data-testid="action-center-tabs"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              data-testid={`action-center-tab-${t.key}`}
              className={
                "px-4 py-2 rounded-md text-sm font-medium transition-colors " +
                (active
                  ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:hover:bg-stone-700")
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "actions" ? (
        <ActionCenter lang="es" />
      ) : (
        <AlertsBoard onNavigate={handleAlertsNavigate} />
      )}
    </AdminShell>
  );
}
